import { FC, useEffect, useMemo, useRef } from 'react';
// 组件
import { PlusOutlined } from '@ant-design/icons';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { Table } from 'antd';
import { ProTable, ProForm, ProNotification, } from '../antd';
import { useLayoutContext } from '../PageLayout/context/LayoutContext';
import EditableRow from './subpage/editableRow';
import MoveEditableRow from './subpage/moveEditableRow';
import EditableCell from './subpage/editableCell';
import TotalTable, { TotalTableActionType } from './subpage/totalTable';
import CopyTableData from '../CopyTableData';
// 方法
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import useColumns from './hooks/useColumns';
import useEdiTableContext from './hooks/useEdiTableContext';
import { filterDataSource, formatDataSource, getRecordCode } from './util';
// 常量
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { STATUS_CREATED, STATUS_CREATED_CODE } from '../constants/constant';
import { EdiTableProps, GROUP_NAME } from './interface';
import './style.less';

/** 可编辑表 */
const EdiTable: FC<EdiTableProps> = props => {
  const {
    actionRef, dataSource: outDataSource, isMove, bordered, isSort, sortFieldName = 'displayOrder', scrollToFirstError,
    validateErrorTip, totalKeys, totalIndex, totalText, height, copyTableDataProps, onValidateError, onValuesChange,
  } = props;
  const tableRef = useRef<HTMLDivElement>(null);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const [form] = ProForm.useForm();
  const { enumData } = useLayoutContext();
  const { dataSource, dispatchEdiTable, } = useEdiTableContext(flagIndex, outDataSource);
  const columns = useColumns(props, dispatchEdiTable, form, tableRef.current, enumData);
  useEffect(() => {
    form.resetFields();
  }, [outDataSource]);
  useEffect(() => {
    handleUpdateTotalData();
  }, [dataSource]);
  /** 可编辑表格展示的数据 */
  const ediTableDataSource = useMemo(() => {
    return filterDataSource(dataSource);
  }, [dataSource]);
  /** 可编辑表字段值更新 */
  const handleValuesChange = (changedValues: any, values: any) => {
    const changeData = changedValues?.[GROUP_NAME];
    // 同步更新当前行的编辑状态
    if (changeData) {
      // onValuesChange
      const [changeKey, changeOption] = Object.entries(changeData)?.[0];
      let [changedField, changedValue] = Object.entries(changeOption as any)[0];
      // 是否需要更新总计
      if (Array.isArray(totalKeys) && totalKeys.length) {
        const findItem = totalKeys.find(v => v.dataIndex === changedField);
        if (findItem) {
          handleUpdateTotalData();
        }
      }
      const currentRowKey = Number(changeKey);
      const findDataSource = dataSource?.find(item => item.key === currentRowKey);
      onValuesChange?.(changedField, changedValue, currentRowKey, { ...findDataSource, ...values[GROUP_NAME][currentRowKey] });
      form.setFieldValue([GROUP_NAME, currentRowKey, STATUS_CREATED_CODE], STATUS_CREATED.UPDATE);
    }
  };
  /** 修改可编辑表单行单项的值 */
  const handleFieldValue = (rowKey: any, name: string, value: any) => {
    if (rowKey === undefined || !name) return;
    form.setFieldValue([GROUP_NAME, rowKey, name], value);
    form.setFieldValue([GROUP_NAME, rowKey, STATUS_CREATED_CODE], STATUS_CREATED.UPDATE);
  };
  /** 修改可编辑表单行多项的值 */
  const handleFieldsValue = (rowKey: any, values: any) => {
    if (rowKey === undefined) return;
    const rowFormData = form.getFieldValue([GROUP_NAME, rowKey]);
    form.setFieldValue([GROUP_NAME, rowKey], { ...rowFormData, ...values, [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE, });
  };
  /** 添加行 */
  const handleAdd = (addItem?: any, isEmpty?: boolean) => {
    dispatchEdiTable('addRow', { addItem, isEmpty });
  };
  /** 追加可编辑表数据 */
  const handleAppend = (appendItem?: any[]) => {
    dispatchEdiTable('append', { appendItem, form, });
  };
  /** 覆盖可编辑表数据 */
  const handleCover = (addItem?: any) => {
    dispatchEdiTable('cover', { addItem });
  };
  /** 获取可编辑表数据 */
  const handleDataSource = (filterDeleteData?: boolean) => {
    const currentDataSource: any[] = [];
    const { effectiveData, deleteData, } = formatDataSource(dataSource);
    effectiveData.forEach((item, index) => {
      const { key, ...restItem } = item;
      // 获取当前行的表单数据
      const rowFormData = form.getFieldValue([GROUP_NAME, key]);
      const newItem = {
        key,
        ...restItem,
        ...rowFormData,
        [STATUS_CREATED_CODE]: getRecordCode(
          item[STATUS_CREATED_CODE],
          rowFormData[STATUS_CREATED_CODE],
        ),
      };
      // 排序
      if (isMove || (isSort && sortFieldName)) newItem[sortFieldName] = index + 1;
      currentDataSource.push(newItem);
    });
    return filterDeleteData ? currentDataSource : [...deleteData, ...currentDataSource];
  };
  /** 删除行 */
  const handleDelete = (deleteDataSource?: any) => {
    if (!deleteDataSource) return;
    let deleteKeys: number[] = [];
    if (Array.isArray(deleteDataSource)) {
      deleteKeys = deleteDataSource.map(v => v.key);
    } else {
      deleteKeys = [deleteDataSource.key];
    }
    if (!deleteKeys.length) return;
    dispatchEdiTable('deleteRow', { deleteKeys, });
  };
  /** 更新统计数据 */
  const handleUpdateTotalData = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const effectiveDataSource = handleDataSource().filter(
        v => v[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE,
      );
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  /** 提交(验证+获取可编辑表数据) */
  const handleSubmit = async () => {
    try {
      const formData = await form.validateFields();
      const submitDataSource: any[] = [];
      const { effectiveData, deleteData, } = formatDataSource(dataSource);
      effectiveData.forEach((item, index) => {
        const { key, ...restItem } = item;
        // 获取当前行的表单数据
        const rowFormData = formData?.[GROUP_NAME]?.[key];
        const newItem = {
          ...restItem,
          ...rowFormData,
          [STATUS_CREATED_CODE]: getRecordCode(
            item[STATUS_CREATED_CODE],
            rowFormData[STATUS_CREATED_CODE],
          ),
        };
        // 排序
        if (isMove || (isSort && sortFieldName)) newItem[sortFieldName] = index + 1;
        submitDataSource.push(newItem);
      });
      return [...deleteData, ...submitDataSource];
    } catch (errorInfo: any) {
      if (validateErrorTip) {
        // 校验错误的行的key集合
        const errorLineKeys: Set<number> = new Set<number>();
        errorInfo?.errorFields?.forEach?.((item: any, index: number) => {
          const currentLine = item?.name?.[1];
          if (scrollToFirstError && index === 0) {
            // 自动滚动到第一个错误字段
            form.scrollToField(item?.name);
          }
          if (currentLine !== undefined) {
            errorLineKeys.add(currentLine);
          }
        });
        // 通过错误行的key获取错误行
        const errorLineIndexs: number[] = [];
        ediTableDataSource.forEach((item, index) => {
          if (errorLineKeys.has(item.key)) {
            errorLineIndexs.push(index + 1)
          }
        })
        // 校验错误提示
        if (onValidateError) {
          onValidateError([...errorLineIndexs]);
        } else {
          ProNotification.warning({
            message: `请完善第${[...errorLineIndexs].join('，')}行数据！`,
          });
        }
      }
      return false;
    }
  };
  /** 清空 */
  const handleEmpty = () => {
    const newDataSource = dataSource?.filter(
      item => item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD,
    );
    dispatchEdiTable('changeDataSource', {
      updateDataSource: newDataSource?.map(item => ({
        ...item,
        [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
      })),
    });
    form.resetFields();
  };
  /** 拖动 */
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over) return;
    const startKey = active.id as number;
    const endKey = over.id as number;
    if (startKey === endKey) return;
    dispatchEdiTable('moveData', { startIndex: startKey - 1, endIndex: endKey - 1});
  };
  /** 更新统计数据 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      handleUpdateTotalData();
    }
  };
  /** 批量修改所有行单项目数据 */
  const handleRowsValue = (field: string, value: any) => {
    const updateDataSource: any[] = [];
    dataSource?.forEach(item => {
      const rowKey = item.key;
      const fieldName = [GROUP_NAME, rowKey, field];
      updateDataSource.push({ ...item, [field]: value, });
      form.setFieldValue(fieldName, value);
      form.setFieldValue([GROUP_NAME, rowKey, STATUS_CREATED_CODE], STATUS_CREATED.UPDATE);
    });
    dispatchEdiTable('changeDataSource', { updateDataSource });
  };
  /** 批量修改所有行多项目数据 */
  const handleRowsValues = (values: any) => {
    const updateDataSource: any[] = [];
    dataSource?.forEach(item => {
      const rowKey = item.key;
      const fieldName = [GROUP_NAME, rowKey];
      updateDataSource.push({ ...item, ...values, });
      const rowFormData = form.getFieldValue(fieldName);
      form.setFieldValue(fieldName, { ...rowFormData, ...values, [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE, });
    });
    dispatchEdiTable('changeDataSource', { updateDataSource });
  };
  /** 修改行数据 */
  const handleChangeRowData = (rowData: any) => {
    dispatchEdiTable('changeRowData', {rowData});
  };
  /** 更新统计数据 */
  const handleUpdateTotal = () => {};
  const formNode = (
    <ProForm
      component={false}
      form={form}
      onValuesChange={handleValuesChange}
      name='edit-table-form'
    >
      <ProTable
        dataSource={ediTableDataSource}
        columns={columns}
        pagination={false}
        bordered={bordered}
        size="small"
        rowKey='key'
        components={{
          body: {
            row: isMove ? MoveEditableRow : EditableRow,
            cell: EditableCell,
          },
        }}
        // @ts-ignore
        onRow={isMove ? (_, index) => {
          return {
            index,
            move: true,
          };
        } : undefined}
        scroll={{
          scrollToFirstRowOnChange: true,
          y: height,
        }}
        summary={() => {
          if (
            Array.isArray(totalKeys) &&
            !!totalKeys.length &&
            Array.isArray(ediTableDataSource) &&
            !!ediTableDataSource.length
          ) {
            return (
              <Table.Summary fixed>
                <TotalTable
                  actionRef={totalTableRef}
                  columns={columns}
                  totalKeys={totalKeys}
                  // dataSource={ediTableDataSource}
                  totalIndex={totalIndex}
                  totalText={totalText}
                />
              </Table.Summary>
            );
          }
        }}
      />
    </ProForm>
  );
  if (actionRef) {
    actionRef.current = {
      add: handleAdd,
      append: handleAppend,
      cover: handleCover,
      getDataSource: handleDataSource,
      delete: handleDelete,
      submit: handleSubmit,
      empty: handleEmpty,
      changeTotal: handleChangeTotal,
      setFieldValue: handleFieldValue,
      setFieldsValue: handleFieldsValue,
      setRowsValues: handleRowsValues,
      setRowsValue: handleRowsValue,
      changeRowData: handleChangeRowData,
      form: form,
      tableElement: tableRef.current,
    };
  }

  return (
    <div ref={tableRef} className='yb-edit-table'>
      {
        isMove ? (
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            <SortableContext
              items={ediTableDataSource.map((_, index) => (index + 1))}
              strategy={verticalListSortingStrategy}
            >
              {formNode}
            </SortableContext>
          </DndContext>
        ) : formNode
      }
      {
        !!copyTableDataProps && (
          <div className='edit-virtual-table-copy'>
            <CopyTableData
              dataSource={ediTableDataSource}
              {...copyTableDataProps}
            />
          </div>
        )
      }
    </div>
  );
};

EdiTable.defaultProps = {
  validateErrorTip: true,
  scrollToFirstError: true,
  operationWidth: 50,
  addIcon: <PlusOutlined />,
  sortFieldName: 'displayOrder',
  sortColumnType: false,
  height: 300,
};

export default EdiTable;
